package org.lennycodes.gradematewebsite.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.icons.fa.FaIcon
import com.varabyte.kobweb.silk.components.icons.fa.IconCategory
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.name
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.lennycodes.gradematewebsite.components.style.CardStyle
import org.lennycodes.gradematewebsite.components.style.EyebrowStyle
import org.lennycodes.gradematewebsite.components.style.GradeMateColors
import org.lennycodes.gradematewebsite.components.style.PrimaryButtonStyle
import org.lennycodes.gradematewebsite.components.style.ResponsiveRowStyle
import org.lennycodes.gradematewebsite.components.style.SectionHeadingStyle
import org.lennycodes.gradematewebsite.components.style.SectionInnerStyle
import org.lennycodes.gradematewebsite.components.style.SectionStyle
import org.lennycodes.gradematewebsite.components.style.SectionSubtitleStyle
import org.lennycodes.gradematewebsite.components.style.toRgba
import org.w3c.dom.events.Event

private const val Ease = "cubic-bezier(0.22, 1, 0.36, 1)"
private const val EaseOutBack = "cubic-bezier(0.34, 1.56, 0.64, 1)"

private fun prefersReducedMotion() = window.matchMedia("(prefers-reduced-motion: reduce)").matches

private fun encodeURIComponent(str: String): String =
    js("encodeURIComponent(str)") as String

@Composable
private fun Reveal(
    id: String,
    modifier: Modifier = Modifier,
    delayMs: Int = 0,
    content: @Composable () -> Unit
) {
    var visible by remember { mutableStateOf(false) }
    val reduce = remember { prefersReducedMotion() }

    DisposableEffect(Unit) {
        val check = {
            val top = document.getElementById(id)?.getBoundingClientRect()?.top
            if (reduce || (top != null && top < window.innerHeight * 0.9)) visible = true
        }
        val onScroll: (Event) -> Unit = { check() }
        window.addEventListener("scroll", onScroll)
        check()
        onDispose { window.removeEventListener("scroll", onScroll) }
    }

    Box(
        modifier
            .id(id)
            .fillMaxWidth()
            .styleModifier {
                val d = if (reduce) 0 else delayMs
                val dur = if (reduce) 0 else 900
                property("opacity", if (visible) "1" else "0")
                property("transform", if (visible) "translateY(0)" else "translateY(32px)")
                property(
                    "transition",
                    "opacity ${dur}ms ease ${d}ms, transform ${dur}ms $Ease ${d}ms"
                )
            }
    ) {
        content()
    }
}

// ---------------------------------------------------------------------------
// Field styling — refined: floating labels, subtle inner shadow, primary glow
// ---------------------------------------------------------------------------
val ContactFieldStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .fontSize(15.px)
            .color(GradeMateColors.TextPrimary)
            .styleModifier {
                property("border", "1px solid ${GradeMateColors.Border.toRgba()}")
                property("border-radius", "14px")
                property("padding", "15px 18px")
                property("outline", "none")
                property("font-family", "inherit")
                property("box-sizing", "border-box")
                property("background-color", GradeMateColors.Surface.toRgba())
                property(
                    "box-shadow",
                    "inset 0 1px 2px rgba(15, 23, 42, 0.04)"
                )
                property(
                    "transition",
                    "border-color 200ms cubic-bezier(0.4, 0, 0.2, 1), " +
                            "box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1), " +
                            "background-color 200ms cubic-bezier(0.4, 0, 0.2, 1), " +
                            "transform 200ms $Ease"
                )
                property("caret-color", GradeMateColors.Primary.toRgba())
                property(
                    "--placeholder-color",
                    GradeMateColors.TextSecondary.toRgba(alpha = 0.5f)
                )
            }
    }
    hover {
        Modifier.styleModifier {
            property("border-color", GradeMateColors.Primary.toRgba(alpha = 0.4f))
            property("background-color", "#ffffff")
        }
    }
    focusVisible {
        Modifier.styleModifier {
            property("border-color", GradeMateColors.Primary.toRgba())
            property("background-color", "#ffffff")
            property(
                "box-shadow",
                "inset 0 1px 2px rgba(15, 23, 42, 0.03), " +
                        "0 0 0 4px ${GradeMateColors.Primary.toRgba(alpha = 0.14f)}, " +
                        "0 8px 24px -12px ${GradeMateColors.Primary.toRgba(alpha = 0.35f)}"
            )
        }
    }
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------
@Composable
fun ContactSection() {
    var name by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var message by remember { mutableStateOf("") }
    var statusMessage by remember { mutableStateOf<String?>(null) }
    var statusIsError by remember { mutableStateOf(false) }
    var sendHovered by remember { mutableStateOf(false) }
    var sendPressed by remember { mutableStateOf(false) }
    val reduce = remember { prefersReducedMotion() }
    fun t(ms: Int) = if (reduce) 0 else ms

    Box(
        SectionStyle
            .toModifier()
            .id("contact")
            .backgroundColor(GradeMateColors.Surface)
            .styleModifier {
                // Layered aurora — three soft pools of brand colour
                property(
                    "background-image",
                    "radial-gradient(45% 55% at 88% 78%, ${GradeMateColors.Primary.toRgba(alpha = 0.09f)}, transparent 65%), " +
                            "radial-gradient(35% 45% at 8% 12%, ${GradeMateColors.Primary.toRgba(alpha = 0.05f)}, transparent 70%), " +
                            "radial-gradient(60% 40% at 50% 100%, ${GradeMateColors.Primary.toRgba(alpha = 0.035f)}, transparent 60%)"
                )
                property("position", "relative")
                property("overflow", "hidden")
            }
    ) {
        Div(
            attrs = ResponsiveRowStyle
                .toModifier()
                .then(SectionInnerStyle.toModifier())
                .styleModifier {
                    property("gap", "clamp(40px, 7vw, 96px)")
                    property("align-items", "center")
                    property("position", "relative")
                    property("z-index", "1")
                }
                .toAttrs()
        ) {

            // -------------------------------------------------------------
            // LEFT — intro + direct links
            // -------------------------------------------------------------
            Reveal("contact-intro") {
                Column(Modifier.fillMaxWidth()) {

                    P(
                        EyebrowStyle
                            .toModifier()
                            .margin(0.px)
                            .styleModifier {
                                property("letter-spacing", "0.16em")
                                property("text-transform", "uppercase")
                                property("font-weight", "700")
                                property("font-size", "12px")
                                property("opacity", "0.72")
                            }
                            .toAttrs()
                    ) {
                        Text("Contact us")
                    }

                    P(
                        SectionHeadingStyle
                            .toModifier()
                            .margin(top = 14.px, bottom = 18.px)
                            .styleModifier {
                                property("font-size", "clamp(32px, 4.5vw, 50px)")
                                property("letter-spacing", "-0.04em")
                                property("line-height", "1.05")
                                property("font-weight", "750")
                            }
                            .toAttrs()
                    ) {
                        Text("Questions or feedback?")
                    }

                    P(
                        SectionSubtitleStyle
                            .toModifier()
                            .margin(0.px)
                            .styleModifier {
                                property("line-height", "1.75")
                                property("font-size", "15px")
                                property("max-width", "420px")
                                property("opacity", "0.78")
                            }
                            .toAttrs()
                    ) {
                        Text(
                            "Send a message and we'll get back to you within " +
                                    "a day — or reach out directly."
                        )
                    }

                    Column(
                        Modifier
                            .margin(top = 40.px)
                            .gap(16.px)
                    ) {
                        ContactLink(
                            href = "mailto:lennycodes2000@gmail.com",
                            iconName = "envelope",
                            iconCategory = IconCategory.SOLID,
                            label = "lennycodes2000@gmail.com",
                            sublabel = "Email"
                        )

                        ContactLink(
                            href = "https://wa.me/255686777093",
                            iconName = "whatsapp",
                            iconCategory = IconCategory.BRAND,
                            label = "+255 686 777 093",
                            sublabel = "WhatsApp"
                        )
                    }
                }
            }

            // -------------------------------------------------------------
            // RIGHT — the form
            // -------------------------------------------------------------
            Reveal("contact-form", delayMs = 150) {
                Column(
                    CardStyle
                        .toModifier()
                        .fillMaxWidth()
                        .gap(20.px)
                        .styleModifier {
                            property("padding", "clamp(26px, 4vw, 40px)")
                            property("border-radius", "24px")
                            property(
                                "background-image",
                                "linear-gradient(155deg, rgba(255,255,255,1) 0%, rgba(250,251,253,0.98) 100%)"
                            )
                            property(
                                "box-shadow",
                                "0 40px 80px -48px rgba(15, 23, 42, 0.35), " +
                                        "0 2px 8px -4px rgba(15, 23, 42, 0.06), " +
                                        "inset 0 1px 0 rgba(255, 255, 255, 0.9)"
                            )
                            property("border", "1px solid rgba(15, 23, 42, 0.06)")
                        }
                ) {

                    // Small header inside the card
                    Column(Modifier.fillMaxWidth().gap(6.px)) {
                        P(
                            Modifier
                                .margin(0.px)
                                .fontSize(18.px)
                                .fontWeight(FontWeight.Bold)
                                .color(GradeMateColors.TextPrimary)
                                .styleModifier {
                                    property("letter-spacing", "-0.02em")
                                    property("line-height", "1.2")
                                }
                                .toAttrs()
                        ) {
                            Text("Send us a message")
                        }
                        P(
                            Modifier
                                .margin(0.px)
                                .fontSize(13.px)
                                .color(GradeMateColors.TextSecondary)
                                .styleModifier { property("line-height", "1.55") }
                                .toAttrs()
                        ) {
                            Text("We usually reply within 24 hours.")
                        }
                    }

                    contactField("Name", "contact-name") {
                        Input(
                            type = InputType.Text,
                            attrs = ContactFieldStyle.toModifier().toAttrs {
                                attr("id", "contact-name")
                                placeholder("Loveness Florence")
                                name("name")
                                value(name)
                                onInput { event -> name = event.value }
                            }
                        )
                    }

                    contactField("Email", "contact-email") {
                        Input(
                            type = InputType.Email,
                            attrs = ContactFieldStyle.toModifier().toAttrs {
                                attr("id", "contact-email")
                                placeholder("loveness@example.com")
                                name("email")
                                value(email)
                                onInput { event -> email = event.value }
                            }
                        )
                    }

                    contactField("Message", "contact-message") {
                        TextArea(
                            attrs = ContactFieldStyle
                                .toModifier()
                                .height(140.px)
                                .styleModifier {
                                    property("resize", "vertical")
                                    property("min-height", "120px")
                                    property("max-height", "280px")
                                }
                                .toAttrs {
                                    attr("id", "contact-message")
                                    placeholder("How can we help?")
                                    name("message")
                                    value(message)
                                    onInput { event -> message = event.value }
                                }
                        )
                    }

                    // Send button
                    Button(
                        onClick = {
                            val emailLooksValid = email.contains("@") && email.contains(".")
                            when {
                                name.isBlank() || message.isBlank() -> {
                                    statusIsError = true
                                    statusMessage = "Please add your name and a message."
                                }
                                !emailLooksValid -> {
                                    statusIsError = true
                                    statusMessage = "Please enter a valid email address."
                                }
                                else -> {
                                    val subject = encodeURIComponent("New message from $name via GradeMate")
                                    val body = encodeURIComponent("From: $name ($email)\n\n$message")
                                    window.location.href =
                                        "mailto:lennycodes2000@gmail.com?subject=$subject&body=$body"
                                    name = ""
                                    email = ""
                                    message = ""
                                    statusIsError = false
                                    statusMessage =
                                        "Your email app is opening with the message ready. Just press send."
                                }
                            }
                        },
                        modifier = PrimaryButtonStyle
                            .toModifier()
                            .onMouseEnter { sendHovered = true }
                            .onMouseLeave {
                                sendHovered = false
                                sendPressed = false
                            }
                            .styleModifier {
                                property("display", "inline-flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("gap", "10px")
                                property("width", "100%")
                                property("height", "54px")
                                property("box-sizing", "border-box")
                                property("border-radius", "14px")
                                property("font-size", "15px")
                                property("font-weight", "650")
                                property("letter-spacing", "-0.01em")
                                property("cursor", "pointer")

                                // Sheen + subtle inner highlight
                                property(
                                    "background-image",
                                    "linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0) 55%)"
                                )

                                // Lift and glow on hover
                                val lift = when {
                                    sendPressed && sendHovered -> "translateY(-1px)"
                                    sendHovered -> "translateY(-3px)"
                                    else -> "translateY(0)"
                                }
                                property("transform", lift)

                                property(
                                    "box-shadow",
                                    when {
                                        sendPressed && sendHovered ->
                                            "0 12px 26px -10px rgba(37, 99, 235, 0.45), " +
                                                    "inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                                        sendHovered ->
                                            "0 22px 40px -12px rgba(37, 99, 235, 0.55), " +
                                                    "inset 0 1px 0 rgba(255, 255, 255, 0.25)"
                                        else ->
                                            "0 10px 24px -10px rgba(37, 99, 235, 0.42), " +
                                                    "inset 0 1px 0 rgba(255, 255, 255, 0.18)"
                                    }
                                )

                                property(
                                    "transition",
                                    "transform ${t(220)}ms $Ease, box-shadow ${t(220)}ms ease"
                                )
                                property("margin-top", "8px")
                            }
                    ) {
                        FaIcon(
                            name = "paper-plane",
                            style = IconCategory.SOLID,
                            modifier = Modifier
                                .fontSize(14.px)
                                .styleModifier {
                                    property(
                                        "transform",
                                        if (sendHovered) "translate(4px, -4px) rotate(-6deg)"
                                        else "translate(0, 0) rotate(0deg)"
                                    )
                                    property(
                                        "transition",
                                        "transform ${t(300)}ms $EaseOutBack"
                                    )
                                }
                        )
                        Text("Send message")
                    }

                    statusMessage?.let { text ->
                        StatusBanner(
                            message = text,
                            isError = statusIsError,
                            onDismiss = { statusMessage = null }
                        )
                    }
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Status banner
// ---------------------------------------------------------------------------
@Composable
private fun StatusBanner(
    message: String,
    isError: Boolean,
    onDismiss: () -> Unit
) {
    var shown by remember(message) { mutableStateOf(false) }
    val reduce = remember { prefersReducedMotion() }

    LaunchedEffect(message) {
        delay(20)
        shown = true
        delay(6000)
        onDismiss()
    }

    val tone = if (isError) "220, 38, 38" else "22, 163, 74"
    val ink = if (isError) "rgb(185, 28, 28)" else "rgb(21, 128, 61)"
    val icon = if (isError) "circle-exclamation" else "circle-check"

    Div(
        attrs = Modifier
            .styleModifier {
                property("display", "flex")
                property("align-items", "flex-start")
                property("gap", "12px")
                property("padding", "14px 18px")
                property("border-radius", "14px")
                property("background-color", "rgba($tone, 0.07)")
                property("border", "1px solid rgba($tone, 0.22)")
                property("color", ink)
                property("font-size", "14px")
                property("font-weight", "500")
                property("line-height", "1.55")
                property("opacity", if (shown) "1" else "0")
                property("transform", if (shown) "translateY(0)" else "translateY(10px)")
                property(
                    "transition",
                    "opacity ${if (reduce) 0 else 400}ms ease, " +
                            "transform ${if (reduce) 0 else 500}ms $Ease"
                )
            }
            .toAttrs { attr("role", if (isError) "alert" else "status") }
    ) {
        FaIcon(
            name = icon,
            style = IconCategory.SOLID,
            modifier = Modifier.fontSize(15.px).styleModifier { property("flex-shrink", "0") }
        )
        Text(message)
    }
}

// ---------------------------------------------------------------------------
// Direct contact link — bigger, richer, with sublabel and animated arrow
// ---------------------------------------------------------------------------
@Composable
private fun ContactLink(
    href: String,
    iconName: String,
    iconCategory: IconCategory,
    label: String,
    sublabel: String,
) {
    var hovered by remember { mutableStateOf(false) }
    val reduce = remember { prefersReducedMotion() }
    fun t(ms: Int) = if (reduce) 0 else ms

    Link(
        href,
        modifier = Modifier
            .fillMaxWidth()
            .onMouseEnter { hovered = true }
            .onMouseLeave { hovered = false }
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "16px")
                property("padding", "14px 16px")
                property("border-radius", "16px")
                property("text-decoration", "none")
                property("background-color",
                    if (hovered) GradeMateColors.Primary.toRgba(alpha = 0.045f)
                    else "transparent"
                )
                property("transform", if (hovered) "translateX(4px)" else "translateX(0)")
                property(
                    "transition",
                    "transform ${t(300)}ms $Ease, background-color ${t(250)}ms ease"
                )
            }
    ) {
        // Circular icon chip
        Box(
            Modifier
                .size(46.px)
                .styleModifier {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("border-radius", "999px")
                    property(
                        "background-color",
                        if (hovered) GradeMateColors.Primary.toRgba()
                        else GradeMateColors.Primary.toRgba(alpha = 0.10f)
                    )
                    property(
                        "color",
                        if (hovered) "#ffffff" else GradeMateColors.Primary.toRgba()
                    )
                    property("flex-shrink", "0")
                    property(
                        "box-shadow",
                        if (hovered)
                            "0 14px 26px -10px ${GradeMateColors.Primary.toRgba(alpha = 0.55f)}"
                        else "none"
                    )
                    property(
                        "transform",
                        if (hovered) "scale(1.05)" else "scale(1)"
                    )
                    property(
                        "transition",
                        "background-color ${t(250)}ms ease, " +
                                "color ${t(250)}ms ease, " +
                                "box-shadow ${t(250)}ms ease, " +
                                "transform ${t(350)}ms $EaseOutBack"
                    )
                }
        ) {
            FaIcon(
                name = iconName,
                style = iconCategory,
                modifier = Modifier.fontSize(17.px)
            )
        }

        // Label + sublabel
        Column(Modifier.gap(2.px)) {
            P(
                Modifier
                    .margin(0.px)
                    .fontSize(11.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(GradeMateColors.TextSecondary)
                    .styleModifier {
                        property("letter-spacing", "0.1em")
                        property("text-transform", "uppercase")
                        property("line-height", "1")
                        property("opacity", "0.7")
                    }
                    .toAttrs()
            ) {
                Text(sublabel)
            }
            P(
                Modifier
                    .margin(0.px)
                    .fontSize(15.px)
                    .fontWeight(FontWeight.SemiBold)
                    .color(GradeMateColors.TextPrimary)
                    .styleModifier {
                        property("letter-spacing", "-0.01em")
                        property("line-height", "1.2")
                    }
                    .toAttrs()
            ) {
                Text(label)
            }
        }

        // Animated arrow
        Div(
            attrs = Modifier
                .styleModifier {
                    property("margin-left", "auto")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("width", "28px")
                    property("height", "28px")
                    property("border-radius", "999px")
                    property(
                        "background-color",
                        if (hovered) GradeMateColors.Primary.toRgba(alpha = 0.12f)
                        else "transparent"
                    )
                    property("color", GradeMateColors.Primary.toRgba())
                    property("opacity", if (hovered) "1" else "0")
                    property(
                        "transform",
                        if (hovered) "translateX(0) scale(1)" else "translateX(-8px) scale(0.9)"
                    )
                    property(
                        "transition",
                        "opacity ${t(220)}ms ease, " +
                                "transform ${t(320)}ms $EaseOutBack, " +
                                "background-color ${t(250)}ms ease"
                    )
                }
                .toAttrs()
        ) {
            FaIcon(
                name = "arrow-right",
                style = IconCategory.SOLID,
                modifier = Modifier.fontSize(11.px)
            )
        }
    }
}

// ---------------------------------------------------------------------------
// Field wrapper
// ---------------------------------------------------------------------------
@Composable
private fun contactField(label: String, fieldId: String, field: @Composable () -> Unit) {
    Column(Modifier.fillMaxWidth().gap(8.px)) {
        Label(
            forId = fieldId,
            attrs = Modifier
                .fontSize(12.px)
                .fontWeight(FontWeight.SemiBold)
                .color(GradeMateColors.TextPrimary)
                .styleModifier {
                    property("letter-spacing", "0.05em")
                    property("text-transform", "uppercase")
                    property("opacity", "0.72")
                }
                .toAttrs()
        ) {
            Text(label)
        }
        field()
    }
}